import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import type { KocPayload } from "@koc/shared";
import { apiKocs } from "../api.js";
import { Badge, Button, Card, Input, Label, PageHeader, Table, Textarea, fmtTime } from "../components/ui.js";

export function KocsPage() {
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["kocs", search], queryFn: () => apiKocs.list(search) });

  return (
    <>
      <PageHeader
        title="KOCs"
        actions={<Button onClick={() => setShowAdd(true)}>+ Add KOC</Button>}
      />
      <div className="mb-3 max-w-sm">
        <Input
          placeholder="Search by KOC name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <Table
        rows={q.data?.items ?? []}
        onRowClick={(r) => navigate({ to: "/kocs/$kocId", params: { kocId: String(r.id) } })}
        columns={[
          { header: "Name", cell: (r) => (
            <Link to="/kocs/$kocId" params={{ kocId: String(r.id) }} className="text-stone-900 font-medium hover:underline">
              {r.name}
            </Link>
          )},
          { header: "Notes", cell: (r) => r.notes ? (
            <span className="text-xs text-stone-600 truncate block max-w-md">{r.notes}</span>
          ) : <span className="text-xs text-stone-400">—</span> },
          { header: "Videos", cell: (r) => r.video_count, className: "text-right tabular-nums w-20" },
          { header: "Projects", cell: (r) => r.project_count, className: "text-right tabular-nums w-20" },
          { header: "Updated", cell: (r) => <span className="text-stone-500 text-xs">{fmtTime(r.updated_at)}</span> },
        ]}
      />
      {showAdd && <KocEditDialog onClose={() => setShowAdd(false)} />}
    </>
  );
}

export function KocEditDialog({
  onClose,
  initial,
  initialId,
}: {
  onClose: () => void;
  initial?: KocPayload;
  initialId?: number;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<KocPayload>(initial ?? { name: "", notes: "" });

  const save = useMutation({
    mutationFn: () => {
      const payload: KocPayload = {
        name: form.name.trim(),
        notes: form.notes?.trim() || null,
      };
      return initialId ? apiKocs.update(initialId, payload).then(() => ({ id: initialId })) : apiKocs.create(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["kocs"] });
      qc.invalidateQueries({ queryKey: ["koc", initialId] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <Card className="w-[32rem] max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-3">{initialId ? "Edit KOC" : "Add KOC"}</h2>
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div>
            <Label>KOC notes</Label>
            <Textarea value={form.notes ?? ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={4} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
          {save.isError && (
            <div className="text-xs text-red-700">
              <Badge tone="red">save failed</Badge> {save.error instanceof Error ? save.error.message : "Unknown error"}
            </div>
          )}
        </form>
      </Card>
    </div>
  );
}
